import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserProvider } from "@/contexts/UserContext";
import { BlogProvider } from "@/contexts/BlogContext";
import PlanSwitcher from "@/components/PlanSwitcher";
import ScrollToTop from "@/components/ScrollToTop";
import AnalyticsRouteTracker from "@/hooks/useAnalyticsPageView";

import PublicLayout from "@/layouts/PublicLayout";
import HeadingMotion from "@/components/ds/HeadingMotion";
import Landing from "@/pages/Landing";
import { OfertaProvider } from '@/contexts/OfertaContext';
import { OfertaRoute, FerramentaRoute } from '@/components/completo/OfertaRoute';
const CompletoLanding = lazy(() => import('@/pages/completo/Landing'));
const CompletoPlanos = lazy(() => import('@/pages/completo/Planos'));
const CompletoQuemSomos = lazy(() => import('@/pages/completo/QuemSomos'));
const CompletoContato = lazy(() => import('@/pages/completo/Contato'));
const CompletoFAQ = lazy(() => import('@/pages/completo/FAQ'));
const CompletoFuncionalidades = lazy(() => import('@/pages/completo/Funcionalidades'));
const CompletoFuncionalidade = lazy(() => import('@/pages/completo/FuncionalidadeDetalhe'));
const CompletoTermos = lazy(() => import('@/pages/completo/TermosDeUso'));
const CompletoPrivacidade = lazy(() => import('@/pages/completo/PoliticaDePrivacidade'));
const CompletoAssinatura = lazy(() => import('@/pages/completo/Assinatura'));

// Lazy-load all non-critical routes to reduce initial bundle and improve LCP.
const AppLayout = lazy(() => import("@/layouts/AppLayout"));
const AdminLayout = lazy(() => import("@/layouts/AdminLayout"));

const Login = lazy(() => import("@/pages/Login"));
const RecuperarSenha = lazy(() => import("@/pages/RecuperarSenha"));
const NovaSenha = lazy(() => import("@/pages/NovaSenha"));
const AuthNovaSenha = lazy(() => import("@/pages/AuthNovaSenha"));
const Cadastro = lazy(() => import("@/pages/Cadastro"));
const Planos = lazy(() => import("@/pages/Planos"));
const PoliticaDePrivacidade = lazy(() => import("@/pages/PoliticaDePrivacidade"));
const TermosDeUso = lazy(() => import("@/pages/TermosDeUso"));
const QuemSomos = lazy(() => import("@/pages/QuemSomos"));
const Contato = lazy(() => import("@/pages/Contato"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Suporte = lazy(() => import("@/pages/Suporte"));
const Funcionalidades = lazy(() => import("@/pages/Funcionalidades"));
const FuncionalidadeDetalhe = lazy(() => import("@/pages/FuncionalidadeDetalhe"));
const ComparativoDePlanos = lazy(() => import("@/pages/ComparativoDePlanos"));
const CentralDeConteudo = lazy(() => import("@/pages/CentralDeConteudo"));
const GuiaDetalhe = lazy(() => import("@/pages/GuiaDetalhe"));
const CategoriaDetalhe = lazy(() => import("@/pages/CategoriaDetalhe"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Projetos = lazy(() => import("@/pages/Projetos"));
const MinhaConta = lazy(() => import("@/pages/MinhaConta"));
const Assinatura = lazy(() => import("@/pages/Assinatura"));
const Analises = lazy(() => import("@/pages/Analises"));
const Estoque = lazy(() => import("@/pages/Estoque"));
const Ajuda = lazy(() => import("@/pages/Ajuda"));
const Calculadora = lazy(() => import("@/pages/Calculadora"));
const Produtos = lazy(() => import("@/pages/Produtos"));
const IARisco = lazy(() => import("@/pages/IARisco"));
const Vitrine = lazy(() => import("@/pages/Vitrine"));
const TricoReceitas = lazy(() => import("@/pages/trico/TricoReceitas"));
const TricoNova = lazy(() => import("@/pages/trico/TricoNova"));
const TricoAmostras = lazy(() => import("@/pages/trico/TricoAmostras"));
const TricoTecnicas = lazy(() => import("@/pages/trico/TricoTecnicas"));
const TricoReceitaDetalhe = lazy(() => import("@/pages/trico/TricoReceitaDetalhe"));
const VitrinePublica = lazy(() => import("@/pages/VitrinePublica"));
const OrcamentoPublico = lazy(() => import("@/pages/OrcamentoPublico"));
const Encomendas = lazy(() => import("@/pages/Encomendas"));
const Clientes = lazy(() => import("@/pages/Clientes"));
const Orcamentos = lazy(() => import("@/pages/Orcamentos"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminUsuarios = lazy(() => import("@/pages/AdminUsuarios"));
const AdminPerfilUsuarios = lazy(() => import("@/pages/AdminPerfilUsuarios"));
const AdminPlanos = lazy(() => import("@/pages/AdminPlanos"));
const AdminCobranca = lazy(() => import("@/pages/AdminCobranca"));
const AdminBlog = lazy(() => import("@/pages/AdminBlog"));
const AdminSuporte = lazy(() => import("@/pages/AdminSuporte"));
const AdminComportamento = lazy(() => import("@/pages/AdminComportamento"));
const AdminIArisco = lazy(() => import("@/pages/AdminIArisco"));
const AdminProjetosExcluidos = lazy(() => import("@/pages/AdminProjetosExcluidos"));
const AuthSucesso = lazy(() => import("@/pages/AuthSucesso"));
const AuthPagamentoSucesso = lazy(() => import("@/pages/AuthPagamentoSucesso"));
const AceitarConvite = lazy(() => import("@/pages/AceitarConvite"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div
    className="min-h-[60vh] flex items-center justify-center"
    role="status"
    aria-live="polite"
    aria-label="Carregando página"
  >
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <OfertaProvider><UserProvider>
          <BlogProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <HeadingMotion />
              <PlanSwitcher />
              <ScrollToTop />
              <AnalyticsRouteTracker />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Public routes */}
                  <Route element={<PublicLayout />}>
                    <Route path="/" element={<OfertaRoute atual={<Landing />} completo={<CompletoLanding />} />} />
                    <Route path="/planos" element={<OfertaRoute atual={<Planos />} completo={<CompletoPlanos />} />} />
                    <Route path="/politica-de-privacidade" element={<OfertaRoute atual={<PoliticaDePrivacidade />} completo={<CompletoPrivacidade />} />} />
                    <Route path="/termos-de-uso" element={<OfertaRoute atual={<TermosDeUso />} completo={<CompletoTermos />} />} />
                    <Route path="/quem-somos" element={<OfertaRoute atual={<QuemSomos />} completo={<CompletoQuemSomos />} />} />
                    <Route path="/contato" element={<OfertaRoute atual={<Contato />} completo={<CompletoContato />} />} />
                    <Route path="/faq" element={<OfertaRoute atual={<FAQ />} completo={<CompletoFAQ />} />} />
                    <Route path="/suporte" element={<Suporte />} />
                    <Route path="/funcionalidades" element={<OfertaRoute atual={<Funcionalidades />} completo={<CompletoFuncionalidades />} />} />
                    <Route path="/funcionalidades/:slug" element={<OfertaRoute atual={<FuncionalidadeDetalhe />} completo={<CompletoFuncionalidade />} />} />
                    <Route path="/comparativo-de-planos" element={<OfertaRoute atual={<ComparativoDePlanos />} completo={<Navigate to="/planos" replace />} />} />
                    <Route path="/central-de-conteudo" element={<CentralDeConteudo />} />
                    <Route path="/conteudos/:slug" element={<GuiaDetalhe />} />
                    <Route path="/categorias/:slug" element={<CategoriaDetalhe />} />
                  </Route>

                  {/* Vitrine pública (rota dinâmica, sem layout) */}
                  <Route path="/vitrine/:slug" element={<VitrinePublica />} />
                  <Route path="/vitrine/:slug/orcamento/:token" element={<OrcamentoPublico />} />
                  <Route path="/o/:token" element={<OrcamentoPublico />} />

                  {/* Redirects de slugs antigos para novos */}
                  <Route path="/conteudos/como-calcular-custo-peca-artesanal" element={<Navigate to="/conteudos/como-calcular-o-custo-de-uma-peca-artesanal" replace />} />
                  <Route path="/conteudos/como-definir-preco-artesanato" element={<Navigate to="/conteudos/como-definir-o-preco-do-artesanato" replace />} />
                  <Route path="/conteudos/como-registrar-tempo-producao" element={<Navigate to="/conteudos/como-registrar-o-tempo-de-producao" replace />} />
                  <Route path="/conteudos/como-organizar-encomendas-artesanato" element={<Navigate to="/conteudos/como-organizar-encomendas-no-artesanato" replace />} />
                  <Route path="/conteudos/como-saber-se-peca-deu-lucro" element={<Navigate to="/conteudos/como-saber-se-uma-peca-deu-lucro" replace />} />
                  <Route path="/conteudos/como-escolher-materiais-projeto-artesanal" element={<Navigate to="/conteudos/como-escolher-materiais-para-cada-projeto-artesanal" replace />} />
                  <Route path="/conteudos/como-acompanhar-andamento-peca" element={<Navigate to="/conteudos/como-acompanhar-melhor-o-andamento-de-uma-peca" replace />} />
                  <Route path="/conteudos/app-ou-planilha-organizar-artesanato" element={<Navigate to="/conteudos/app-ou-planilha-para-organizar-o-artesanato" replace />} />
                  <Route path="/conteudos/como-sair-registro-basico-acompanhamento-profundo" element={<Navigate to="/conteudos/como-sair-do-registro-basico-para-um-acompanhamento-mais-profundo" replace />} />
                  <Route path="/conteudos/como-organizar-clientes-pedidos-artesanato" element={<Navigate to="/conteudos/como-organizar-clientes-e-pedidos-no-artesanato" replace />} />
                  <Route path="/conteudos/como-calcular-valor-hora-trabalho-artesanal" element={<Navigate to="/conteudos/como-calcular-o-valor-da-sua-hora-de-trabalho" replace />} />

                  <Route path="/login" element={<Login />} />
                  <Route path="/recuperar-senha" element={<RecuperarSenha />} />
                  <Route path="/nova-senha" element={<Navigate to="/auth/nova-senha" replace />} />
                  <Route path="/auth/nova-senha" element={<AuthNovaSenha />} />
                  <Route path="/cadastro" element={<Cadastro />} />
                  <Route path="/auth/sucesso" element={<AuthSucesso />} />
                  <Route path="/auth/pagamento-sucesso" element={<AuthPagamentoSucesso />} />
                  <Route path="/aceitar-convite" element={<AceitarConvite />} />

                  {/* Redirects */}
                  <Route path="/entrar" element={<Navigate to="/login" replace />} />
                  <Route path="/minha-conta" element={<Navigate to="/painel/minha-conta" replace />} />
                  <Route path="/minha-conta/assinatura" element={<Navigate to="/painel/assinatura" replace />} />

                  {/* Authenticated routes */}
                  <Route path="/painel" element={<AppLayout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="projetos" element={<Projetos />} />
                    <Route path="analises" element={<Analises />} />
                    <Route path="estoque" element={<Estoque />} />

                    <Route path="encomendas" element={<Encomendas />} />
                    <Route path="orcamentos" element={<Orcamentos />} />
                    <Route path="clientes" element={<Clientes />} />
                    <Route path="minha-conta" element={<MinhaConta />} />
                    <Route path="assinatura" element={<OfertaRoute atual={<Assinatura />} completo={<CompletoAssinatura />} />} />
                    <Route path="calculadora" element={<Calculadora />} />
                    <Route path="produtos" element={<Produtos />} />
                    <Route path="iarisco" element={<IARisco />} />
                    <Route path="vitrine" element={<Vitrine />} />
                    <Route path="trico" element={<FerramentaRoute><TricoReceitas /></FerramentaRoute>} />
                    <Route path="trico/nova" element={<FerramentaRoute><TricoNova /></FerramentaRoute>} />
                    <Route path="trico/amostras" element={<FerramentaRoute><TricoAmostras /></FerramentaRoute>} />
                    <Route path="trico/tecnicas" element={<FerramentaRoute><TricoTecnicas /></FerramentaRoute>} />
                    <Route path="trico/:id" element={<FerramentaRoute><TricoReceitaDetalhe /></FerramentaRoute>} />
                    <Route path="ajuda" element={<Ajuda />} />
                  </Route>

                  {/* Admin routes */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<Admin />} />
                    <Route path="usuarios" element={<AdminUsuarios />} />
                    <Route path="perfil-usuarios" element={<AdminPerfilUsuarios />} />
                    <Route path="planos" element={<AdminPlanos />} />
                    <Route path="cobranca" element={<AdminCobranca />} />
                    <Route path="conteudo" element={<AdminBlog />} />
                    <Route path="suporte" element={<AdminSuporte />} />
                    <Route path="comportamento" element={<AdminComportamento />} />
                    <Route path="iarisco" element={<AdminIArisco />} />
                    <Route path="projetos-excluidos" element={<AdminProjetosExcluidos />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </BlogProvider>
        </UserProvider></OfertaProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
